import React from 'react';
import classNames from 'classnames';
import { sendEvent } from '@JohnLewisPartnership/experiments-utils';
import styles from './DesktopMenuTabs.scss';
import { formatTestId } from '../../../utils/test-id/format-testid';
import { Category } from '../constants';

type DesktopMenuTabsProps = {
  tabsToDisplay?: readonly string[];
  activeTab?: string;
  isMenuOpen?: boolean;
  onTabFocus?: (tab: string) => void;
  onActiveTabChange: (tab: string) => void;
  onActiveTabKeyDown?: (
    event: React.KeyboardEvent<HTMLButtonElement>,
    tab: string
  ) => void;
  isTouch?: boolean;
};

export const DesktopMenuTabs = ({
  tabsToDisplay = [],
  activeTab,
  isMenuOpen = false,
  onTabFocus,
  onActiveTabChange,
  onActiveTabKeyDown,
  isTouch = false,
}: DesktopMenuTabsProps) => {
  const handleTabClick = (tab: string, tabName: string) => {
    if (activeTab === tab) {
      return;
    }
    onActiveTabChange(tab);
    sendEvent(
      `navigation.tab.${tabName.replace(/\s+/g, '-').toLowerCase()}.click`
    );
  };

  return (
    <ul
      className={styles.desktopMenuTabs}
      data-testid="navigation-menu-tabs"
      suppressHydrationWarning={true}
    >
      {tabsToDisplay.map(tab => {
        const tabName =
          tab === Category.SERVICES_EXPERIENCES ? Category.SERVICES : tab;
        return (
          <li
            key={tabName}
            className={classNames(styles.desktopMenuTab)}
            {...(!isTouch && {
              onMouseEnter: () => handleTabClick(tab, tabName),
            })}
            suppressHydrationWarning={true}
          >
            <button
              className={classNames(styles.desktopMenuTabButton, {
                [styles.desktopMenuTabButton__active]: tabName === activeTab,
              })}
              {...(isTouch && { onClick: () => handleTabClick(tab, tabName) })}
              onFocus={() => onTabFocus?.(tab)}
              onKeyDown={event => onActiveTabKeyDown?.(event, tab)}
              tabIndex={isMenuOpen ? (tab === activeTab ? 0 : -1) : 0}
              data-current-tab={tab === activeTab ? 'true' : 'false'}
              data-testid={formatTestId(tabName, 'nav-menu-tab-')}
              aria-haspopup="true"
              aria-expanded={isMenuOpen && tab === activeTab ? 'true' : 'false'}
              suppressHydrationWarning={true}
            >
              {tabName}
            </button>
          </li>
        );
      })}
    </ul>
  );
};
